import useLocaleLink from 'hooks/useLocaleLink';
import { useCallback, useEffect, useRef } from 'react';
import type { CategoryNavItem } from 'types/help-center';

interface CategoryTabsProps {
  categories: CategoryNavItem[];
  activeSlug: string;
}

export default function CategoryTabs({ categories, activeSlug }: CategoryTabsProps) {
  const localeLink = useLocaleLink();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());

  const scrollToCenter = useCallback((slug: string) => {
    const container = containerRef.current;
    const el = itemRefs.current.get(slug);
    if (!container || !el) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.left - containerRect.left + elRect.width / 2 - containerRect.width / 2;

    container.scrollBy({ left: offset, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToCenter(activeSlug);
  }, [activeSlug, scrollToCenter]);

  const setItemRef = useCallback((slug: string, el: HTMLAnchorElement | null) => {
    if (el) {
      itemRefs.current.set(slug, el);
    } else {
      itemRefs.current.delete(slug);
    }
  }, []);

  return (
    <div ref={containerRef} className="scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 py-2 pad:hidden">
      {categories.map((cat) => {
        const isActive = cat.slug === activeSlug;
        return (
          <a
            key={cat.slug}
            ref={(el) => setItemRef(cat.slug, el)}
            href={`${localeLink}${cat.href}`}
            className={`shrink-0 whitespace-nowrap rounded-md px-3 py-[5px] text-sm font-medium leading-[18px] transition-colors ${
              isActive
                ? 'border border-base-primary text-base-label'
                : 'bg-base-fill-secondary text-base-label hover:bg-base-hover-fill-secondary'
            }`}
          >
            {cat.title}
          </a>
        );
      })}
    </div>
  );
}
